import React from "react";
import {
  Box,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import type { ShareVisibility } from "src/api/shares";

interface VisibilityControlProps {
  visibility: ShareVisibility;
  onChange: (next: ShareVisibility) => void;
  disabled: boolean;
  tid: string;
  publicCaption: string;
  privateCaption: string;
  /** The caller's role cannot publish this resource type: the public radio
   *  is not offered. A resource that is already public still shows that
   *  state, read-only, so the private radio remains a way back. */
  publicOptionHidden?: boolean;
  /** Why the public option is missing, shown under the radios. */
  hiddenNote?: string;
  /** The server has reported the visibility. Until then the read-only
   *  public line stays hidden rather than guess. */
  visibilityKnown?: boolean;
}

const VisibilityControl: React.FC<VisibilityControlProps> = ({
  visibility,
  onChange,
  disabled,
  tid,
  publicCaption,
  privateCaption,
  publicOptionHidden = false,
  hiddenNote,
  visibilityKnown = true,
}) => (
  <Box sx={{ mb: 2 }}>
    <FormLabel sx={{ fontSize: 14, fontWeight: 600 }}>Who can access</FormLabel>
    <RadioGroup
      value={visibility}
      onChange={(e) => onChange(e.target.value as ShareVisibility)}
    >
      {publicOptionHidden ? (
        visibilityKnown &&
        visibility === "public" && (
          <Box sx={{ py: 1 }} data-testid={`${tid}-visibility-public-static`}>
            <Typography variant="body2">
              Anyone in the organization (published by an administrator)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {publicCaption}
            </Typography>
          </Box>
        )
      ) : (
        <FormControlLabel
          value="public"
          disabled={disabled}
          control={
            <Radio size="small" data-testid={`${tid}-visibility-public`} />
          }
          label={
            <Box>
              <Typography variant="body2">
                Anyone in the organization
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {publicCaption}
              </Typography>
            </Box>
          }
        />
      )}
      <FormControlLabel
        value="private"
        disabled={disabled}
        control={
          <Radio size="small" data-testid={`${tid}-visibility-private`} />
        }
        label={
          <Box>
            <Typography variant="body2">
              Only specific people &amp; groups
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {privateCaption}
            </Typography>
          </Box>
        }
      />
    </RadioGroup>
    {publicOptionHidden && hiddenNote && (
      <FormHelperText data-testid={`${tid}-visibility-hidden-note`}>
        {hiddenNote}
      </FormHelperText>
    )}
  </Box>
);

export default VisibilityControl;
